import {cloneDeep, isArray, isBoolean, isEmpty, isNil, isNumber, isPlainObject, isString, pull} from 'lodash';
import {SpecContext} from '../spec/SpecModel';
import {applyEach, check, checkField as checkCatalogField, mergeFormat, OpError, validateFormat as validateFormatSpec} from '../spec/SpecOps';
import {ApplySpecOpsResult, RejectedSpecOp} from '../spec/SpecTypes';
import {
    AG_OPTION_KEYS,
    AGGREGATORS,
    AUTOSIZE_FILL_MODES,
    AUTOSIZE_MODES,
    CELL_STYLE_VALUES,
    CellStyle,
    CONDITION_OPERATORS,
    DEFAULT_GRID_SPEC,
    GRID_SPEC_VERSION,
    GridSpec,
    GridSpecAgOptions,
    GridSpecCapabilities,
    GridSpecColumn,
    GridSpecColumnGroup,
    GridSpecColumnOrGroup,
    GridSpecFilter,
    GridSpecSorter,
    isColumnGroup,
    StyleRule
} from './GridSpecTypes';

/**
 * GridSpecOp - the one vocabulary for changing a GridSpec.
 *
 * The manual configurator, the AI assistant and grid interactions all produce these ops, and
 * `applyOps()` is the single reducer that applies them. Ops are intent-level and set-style (they name
 * fields, not positions in a stale copy of the spec), so AI ops computed against a spec the user has
 * since edited still apply cleanly on top of those edits.
 */
export type GridSpecOp =
    | {op: 'setOptions'; options: GridSpecOptions}
    | {op: 'setAgOptions'; agOptions: GridSpecAgOptions}
    | {op: 'addColumn'; column: GridSpecColumn; groupId?: string; index?: number}
    | {op: 'updateColumn'; field: string; changes: GridSpecColumnChanges}
    | {op: 'removeColumn'; field: string}
    | {op: 'moveColumn'; field: string; toIndex: number}
    | {op: 'setColumnLayout'; layout: ColumnLayoutEntry[]}
    | {op: 'addColumnGroup'; groupId: string; headerName: string; headerAlign?: GridSpecColumnGroup['headerAlign']; fields?: string[]}
    | {op: 'updateColumnGroup'; groupId: string; changes: Partial<Pick<GridSpecColumnGroup, 'headerName' | 'headerAlign'>>}
    | {op: 'removeColumnGroup'; groupId: string}
    | {op: 'setColumnGroup'; field: string; groupId: string}
    | {op: 'setSort'; sortBy: GridSpecSorter[]}
    | {op: 'setGroupBy'; groupBy: string[]}
    | {op: 'setFilters'; filters: GridSpecFilter[]}
    | {op: 'setRowStyleRules'; rules: StyleRule[]}
    | {op: 'setCellStyleRules'; field: string; rules: StyleRule[]}
    | {op: 'replaceSpec'; spec: GridSpec};

export type GridSpecOptions = Partial<
    Pick<GridSpec, 'showLeafRows' | 'totals' | 'rowBorders' | 'cellBorders' | 'stripeRows' | 'sizingMode' | 'autosizeMode' | 'autosizeFillMode'>
>;

/** Column changes - a `null` value removes the setting. `format` is merged into the existing format. */
export type GridSpecColumnChanges = {[K in keyof Omit<GridSpecColumn, 'field'>]?: GridSpecColumn[K] | null};

/** Column layout as reported by the grid itself, in display order. */
export interface ColumnLayoutEntry {
    field: string;
    width?: number;
    hidden?: boolean;
    pinned?: 'left' | 'right';
}

export type GridSpecContext = SpecContext<GridSpecCapabilities>;
export type RejectedOp = RejectedSpecOp<GridSpecOp>;
export type ApplyOpsResult = ApplySpecOpsResult<GridSpec, GridSpecOp>;

/**
 * Apply ops to a spec, returning a new spec. Each op is validated against the catalog and host
 * capabilities; invalid ops are skipped and reported, the rest still apply.
 */
export function applyOps(spec: GridSpec, ops: GridSpecOp[], ctx: GridSpecContext): ApplyOpsResult {
    return applyEach(spec, ops, (draft, op) => applyOp(draft, op, ctx));
}

/** Fill in missing properties (e.g. from older persisted state) and upgrade older versions. */
export function normalizeSpec(spec: Partial<GridSpec>, defaultSpec: GridSpec): GridSpec {
    if (!spec) return cloneDeep(defaultSpec);
    const ret = cloneDeep({
        ...DEFAULT_GRID_SPEC,
        ...defaultSpec,
        ...spec,
        columns: spec.columns ?? defaultSpec.columns,
        agOptions: {...spec.agOptions},
        version: GRID_SPEC_VERSION
    });
    if ((spec.version ?? 1) < 2) upgradeStyleRules(ret);
    return ret;
}

/** v1 -> v2: style rules named one of six fixed classes, now they carry a CellStyle. */
const V1_STYLE_CLASSES: Record<string, CellStyle> = {
    positive: {color: 'positive'},
    negative: {color: 'negative'},
    highlight: {background: 'primary'},
    muted: {color: 'muted'},
    bold: {fontWeight: 'bold'},
    warn: {background: 'warning'}
};

function upgradeStyleRules(spec: GridSpec) {
    const upgrade = (rules: StyleRule[]) =>
        rules?.map(r => {
            const {className, ...rest} = r as StyleRule & {className?: string};
            return className && !r.style ? {...rest, style: {...V1_STYLE_CLASSES[className]}} : r;
        });

    spec.rowClassRules = upgrade(spec.rowClassRules) ?? [];
    leafColumns(spec).forEach(c => {
        if (c.cellClassRules) c.cellClassRules = upgrade(c.cellClassRules);
    });
}

//------------------------
// Spec helpers
//------------------------
export function leafColumns(spec: GridSpec): GridSpecColumn[] {
    return spec.columns.flatMap(c => (isColumnGroup(c) ? c.children : [c]));
}

export function columnGroups(spec: GridSpec): GridSpecColumnGroup[] {
    return spec.columns.filter(isColumnGroup);
}

export function findColumn(spec: GridSpec, field: string): GridSpecColumn {
    return leafColumns(spec).find(c => c.field === field) ?? null;
}

export function findParentGroup(spec: GridSpec, field: string): GridSpecColumnGroup {
    return columnGroups(spec).find(g => g.children.some(c => c.field === field)) ?? null;
}

/** Short, human-readable description of ops - used for undo labels and the assistant log. */
export function describeOps(ops: GridSpecOp[]): string {
    return ops.map(describeOp).join(', ');
}

function describeOp(o: GridSpecOp): string {
    switch (o.op) {
        case 'setOptions':
            return Object.entries(o.options)
                .map(([k, v]) => `${k}: ${v}`)
                .join(', ');
        case 'setAgOptions':
            return 'grid options';
        case 'addColumn':
            return `add ${o.column.field}`;
        case 'updateColumn':
            return `update ${o.field}`;
        case 'removeColumn':
            return `remove ${o.field}`;
        case 'moveColumn':
            return `move ${o.field}`;
        case 'setColumnLayout':
            return 'column layout';
        case 'addColumnGroup':
            return `add group ${o.headerName}`;
        case 'updateColumnGroup':
            return `update group ${o.groupId}`;
        case 'removeColumnGroup':
            return `remove group ${o.groupId}`;
        case 'setColumnGroup':
            return o.groupId ? `move ${o.field} to ${o.groupId}` : `ungroup ${o.field}`;
        case 'setSort':
            return isEmpty(o.sortBy) ? 'clear sort' : `sort by ${o.sortBy.map(s => `${s.colId} ${s.sort}`).join(', ')}`;
        case 'setGroupBy':
            return isEmpty(o.groupBy) ? 'ungroup' : `group by ${o.groupBy.join(', ')}`;
        case 'setFilters':
            return isEmpty(o.filters) ? 'clear filters' : `${o.filters.length} filter(s)`;
        case 'setRowStyleRules':
            return 'row styles';
        case 'setCellStyleRules':
            return `${o.field} cell styles`;
        case 'replaceSpec':
            return 'replace grid';
    }
}

//------------------------
// Reducer
//------------------------
function applyOp(spec: GridSpec, o: GridSpecOp, ctx: GridSpecContext) {
    switch (o.op) {
        case 'setOptions':
            return setOptions(spec, o.options, ctx);
        case 'setAgOptions':
            return setAgOptions(spec, o.agOptions);
        case 'addColumn':
            return addColumn(spec, o.column, o.groupId, o.index, ctx);
        case 'updateColumn':
            return updateColumn(spec, o.field, o.changes, ctx);
        case 'removeColumn':
            return removeColumn(spec, o.field);
        case 'moveColumn':
            return moveColumn(spec, o.field, o.toIndex);
        case 'setColumnLayout':
            return setColumnLayout(spec, o.layout);
        case 'addColumnGroup':
            return addColumnGroup(spec, o.groupId, o.headerName, o.headerAlign, o.fields);
        case 'updateColumnGroup':
            return updateColumnGroup(spec, o.groupId, o.changes);
        case 'removeColumnGroup':
            return removeColumnGroup(spec, o.groupId);
        case 'setColumnGroup':
            return setColumnGroup(spec, o.field, o.groupId);
        case 'setSort':
            return setSort(spec, o.sortBy, ctx);
        case 'setGroupBy':
            return setGroupBy(spec, o.groupBy, ctx);
        case 'setFilters':
            return setFilters(spec, o.filters, ctx);
        case 'setRowStyleRules':
            return setRowStyleRules(spec, o.rules, ctx);
        case 'setCellStyleRules':
            return setCellStyleRules(spec, o.field, o.rules, ctx);
        case 'replaceSpec':
            return replaceSpec(spec, o.spec, ctx);
        default:
            throw new OpError(`Unknown op '${(o as any).op}'`);
    }
}

function setOptions(spec: GridSpec, options: GridSpecOptions, ctx: GridSpecContext) {
    check(isPlainObject(options), 'options must be an object');
    Object.entries(options).forEach(([key, v]) => {
        switch (key) {
            case 'showLeafRows':
                check(ctx.capabilities.leafRows, 'This grid does not support leaf rows');
                check(isBoolean(v), 'showLeafRows must be boolean');
                break;
            case 'totals':
                check(ctx.capabilities.totals, 'This grid does not support totals');
                check(v === false || v === 'top' || v === 'bottom', "totals must be false, 'top' or 'bottom'");
                break;
            case 'rowBorders':
            case 'cellBorders':
            case 'stripeRows':
                check(isBoolean(v), `${key} must be boolean`);
                break;
            case 'sizingMode':
                check(v === null || ['tiny', 'compact', 'standard', 'large'].includes(v as string), 'Unknown sizingMode');
                break;
            case 'autosizeMode':
                check(v === null || AUTOSIZE_MODES.includes(v as any), `autosizeMode must be null or one of ${AUTOSIZE_MODES.join(', ')}`);
                break;
            case 'autosizeFillMode':
                check(v === null || AUTOSIZE_FILL_MODES.includes(v as any), `autosizeFillMode must be null or one of ${AUTOSIZE_FILL_MODES.join(', ')}`);
                break;
            default:
                throw new OpError(`Unknown option '${key}'`);
        }
        (spec as any)[key] = v;
    });
}

function setAgOptions(spec: GridSpec, agOptions: GridSpecAgOptions) {
    check(isPlainObject(agOptions), 'agOptions must be an object');
    Object.entries(agOptions).forEach(([key, v]) => {
        check(AG_OPTION_KEYS.includes(key as any), `ag-Grid option '${key}' is not supported`);
        check(v === null || isBoolean(v) || isNumber(v), `ag-Grid option '${key}' must be a boolean or number`);
        if (v === null) delete spec.agOptions[key];
        else spec.agOptions[key] = v;
    });
}

function addColumn(spec: GridSpec, column: GridSpecColumn, groupId: string, index: number, ctx: GridSpecContext) {
    check(isPlainObject(column), 'column must be an object');
    checkField(column.field, ctx);

    // Adding a column that already exists means "show it, with these settings"
    if (findColumn(spec, column.field)) {
        const {field, ...changes} = column;
        updateColumn(spec, field, {hidden: false, ...changes}, ctx);
        if (!isNil(groupId)) setColumnGroup(spec, field, groupId);
        return;
    }

    validateColumn(column, ctx);
    const container = groupId ? requireGroup(spec, groupId).children : spec.columns;
    insertAt(container, column, index);
}

function updateColumn(spec: GridSpec, field: string, changes: GridSpecColumnChanges, ctx: GridSpecContext) {
    const col = requireColumn(spec, field);
    check(isPlainObject(changes), 'changes must be an object');
    check(!('field' in changes), 'A column field cannot be changed - remove and add a column instead');

    Object.entries(changes).forEach(([key, v]) => {
        if (v === null || v === undefined) {
            delete col[key];
        } else if (key === 'format') {
            // Merge, so e.g. {format: {precision: 2}} keeps the existing number style
            col.format = mergeFormat(col.format, v as GridSpecColumn['format']);
        } else {
            col[key] = v;
        }
    });
    if (col.flex) delete col.width;

    validateColumn(col, ctx);
}

function removeColumn(spec: GridSpec, field: string) {
    requireColumn(spec, field);
    removeFromParent(spec, field);
    spec.sortBy = spec.sortBy.filter(s => s.colId !== field);
}

function moveColumn(spec: GridSpec, field: string, toIndex: number) {
    check(isNumber(toIndex) && toIndex >= 0, 'toIndex must be a non-negative number');
    const col = requireColumn(spec, field),
        origGroup = findParentGroup(spec, field);

    removeFromParent(spec, field);

    // Insert next to the leaf now at toIndex, preferring to stay in the original group at a boundary
    const leaves = leafColumns(spec),
        prev = leaves[toIndex - 1],
        next = leaves[toIndex],
        parentOf = (c: GridSpecColumn) => (c ? findParentGroup(spec, c.field) : undefined);

    if (prev && parentOf(prev) === origGroup) {
        insertAfter(spec, prev.field, col);
    } else if (next) {
        insertBefore(spec, next.field, col);
    } else if (prev) {
        insertAfter(spec, prev.field, col);
    } else {
        spec.columns.push(col);
    }
}

function setColumnLayout(spec: GridSpec, layout: ColumnLayoutEntry[]) {
    check(isArray(layout), 'layout must be an array');
    const order = new Map(layout.map((e, idx) => [e.field, idx])),
        orderOf = (c: GridSpecColumnOrGroup) => (isColumnGroup(c) ? Math.min(...c.children.map(orderOf)) : (order.get(c.field) ?? Number.MAX_SAFE_INTEGER)),
        byOrder = (a: GridSpecColumnOrGroup, b: GridSpecColumnOrGroup) => orderOf(a) - orderOf(b);

    layout.forEach(({field, width, hidden, pinned}) => {
        const col = findColumn(spec, field);
        if (!col) return;
        if (isNumber(width) && !col.flex) col.width = Math.round(width);
        if (isBoolean(hidden)) {
            if (hidden) col.hidden = true;
            else delete col.hidden;
        }
        if (pinned) col.pinned = pinned;
        else delete col.pinned;
    });

    columnGroups(spec).forEach(g => g.children.sort(byOrder));
    spec.columns.sort(byOrder);
}

function addColumnGroup(spec: GridSpec, groupId: string, headerName: string, headerAlign: GridSpecColumnGroup['headerAlign'], fields: string[] = []) {
    check(isString(groupId) && !isEmpty(groupId), 'groupId is required');
    check(!columnGroups(spec).some(g => g.groupId === groupId), `Column group '${groupId}' already exists`);
    check(!findColumn(spec, groupId), `'${groupId}' is already used as a column id`);
    fields.forEach(f => requireColumn(spec, f));

    const group: GridSpecColumnGroup = {groupId, headerName: headerName ?? groupId, children: []};
    if (headerAlign) group.headerAlign = headerAlign;

    // Place the group where its first column was
    const firstIdx = fields.length ? leafIndex(spec, fields[0]) : -1;
    const cols = fields.map(f => {
        const col = findColumn(spec, f);
        removeFromParent(spec, f);
        return col;
    });
    group.children = cols;

    if (firstIdx < 0) {
        spec.columns.push(group);
    } else {
        const next = leafColumns(spec)[firstIdx],
            topIdx = next ? spec.columns.findIndex(c => c === next || (isColumnGroup(c) && c.children.includes(next))) : -1;
        insertAt(spec.columns, group, topIdx < 0 ? undefined : topIdx);
    }
}

function updateColumnGroup(spec: GridSpec, groupId: string, changes: Partial<Pick<GridSpecColumnGroup, 'headerName' | 'headerAlign'>>) {
    const group = requireGroup(spec, groupId);
    check(isPlainObject(changes), 'changes must be an object');
    if (!isNil(changes.headerName)) group.headerName = changes.headerName;
    if (changes.headerAlign !== undefined) {
        check(changes.headerAlign === null || isAlign(changes.headerAlign), 'Unknown headerAlign');
        if (changes.headerAlign) group.headerAlign = changes.headerAlign;
        else delete group.headerAlign;
    }
}

/** Remove a group, keeping its columns in place at the top level. */
function removeColumnGroup(spec: GridSpec, groupId: string) {
    const group = requireGroup(spec, groupId),
        idx = spec.columns.indexOf(group);
    spec.columns.splice(idx, 1, ...group.children);
}

/** Move a column into a group (appended), or out of its group (placed right after it) for a null groupId. */
function setColumnGroup(spec: GridSpec, field: string, groupId: string) {
    const col = requireColumn(spec, field),
        current = findParentGroup(spec, field);

    if ((current?.groupId ?? null) === (groupId ?? null)) return;

    if (groupId) {
        const target = requireGroup(spec, groupId);
        removeFromParent(spec, field);
        target.children.push(col);
    } else {
        removeFromParent(spec, field);
        insertAt(spec.columns, col, spec.columns.indexOf(current) + 1);
    }
}

function setSort(spec: GridSpec, sortBy: GridSpecSorter[], ctx: GridSpecContext) {
    check(ctx.capabilities.sorting !== false, 'This grid sorts itself');
    check(isArray(sortBy), 'sortBy must be an array');
    sortBy.forEach(s => {
        check(isPlainObject(s), 'Malformed sorter');
        requireColumn(spec, s.colId);
        check(s.sort === 'asc' || s.sort === 'desc', "sort must be 'asc' or 'desc'");
    });
    spec.sortBy = sortBy.map(({colId, sort, abs}) => (abs ? {colId, sort, abs: true} : {colId, sort}));
}

function setGroupBy(spec: GridSpec, groupBy: string[], ctx: GridSpecContext) {
    check(ctx.capabilities.grouping !== 'none', 'This grid does not support grouping');
    check(isArray(groupBy), 'groupBy must be an array');
    groupBy.forEach(f => {
        const entry = checkField(f, ctx);
        check(entry.isDimension, `'${f}' cannot be used for grouping`);
    });

    // ag-Grid row grouping works off columns - make sure one exists (hidden) for each group field
    if (ctx.capabilities.grouping === 'flat') {
        groupBy.filter(f => !findColumn(spec, f)).forEach(field => spec.columns.push({field, hidden: true}));
    }

    spec.groupBy = [...groupBy];
}

function setFilters(spec: GridSpec, filters: GridSpecFilter[], ctx: GridSpecContext) {
    check(ctx.capabilities.filtering !== 'none', 'This grid does not support filtering');
    check(isArray(filters), 'filters must be an array');
    filters.forEach(f => validateCondition(f, ctx));
    spec.filters = filters.map(({field, op, value}) => ({field, op, value}));
}

function setRowStyleRules(spec: GridSpec, rules: StyleRule[], ctx: GridSpecContext) {
    check(isArray(rules), 'rules must be an array');
    rules.forEach(r => validateStyleRule(r, ctx));
    spec.rowClassRules = cloneDeep(rules);
}

/** Replace a column's cell style rules - same as updateColumn {cellClassRules}, named for symmetry with row rules. */
function setCellStyleRules(spec: GridSpec, field: string, rules: StyleRule[], ctx: GridSpecContext) {
    check(isArray(rules), 'rules must be an array');
    updateColumn(spec, field, {cellClassRules: isEmpty(rules) ? null : cloneDeep(rules)}, ctx);
}

function replaceSpec(spec: GridSpec, next: GridSpec, ctx: GridSpecContext) {
    check(isPlainObject(next) && isArray(next.columns), 'spec must be a GridSpec');

    const clean = normalizeSpec(next, spec);
    leafColumns(clean).forEach(c => {
        checkField(c.field, ctx);
        validateColumn(c, ctx);
    });
    clean.sortBy.forEach(s => requireColumn(clean, s.colId));
    clean.filters.forEach(f => validateCondition(f, ctx));
    clean.rowClassRules.forEach(r => validateStyleRule(r, ctx));
    clean.groupBy.forEach(f => checkField(f, ctx));

    Object.keys(spec).forEach(k => delete spec[k]);
    Object.assign(spec, clean);
}

//------------------------
// Validation
//------------------------
function checkField(field: string, ctx: GridSpecContext) {
    return checkCatalogField(field, ctx.catalog);
}

function isAlign(v: any) {
    return ['left', 'center', 'right'].includes(v);
}

function validateColumn(c: GridSpecColumn, ctx: GridSpecContext) {
    const {field} = c;
    if (!isNil(c.align)) check(isAlign(c.align), `${field}: unknown align '${c.align}'`);
    if (!isNil(c.headerAlign)) check(isAlign(c.headerAlign), `${field}: unknown headerAlign '${c.headerAlign}'`);
    if (!isNil(c.pinned)) check(c.pinned === 'left' || c.pinned === 'right', `${field}: pinned must be 'left' or 'right'`);
    if (!isNil(c.width)) check(isNumber(c.width) && c.width > 0, `${field}: width must be a positive number`);
    if (!isNil(c.aggregator)) check(AGGREGATORS.includes(c.aggregator), `${field}: unknown aggregator '${c.aggregator}'`);
    if (!isNil(c.format)) validateFormatSpec(c.format, field);
    (c.cellClassRules ?? []).forEach(r => validateStyleRule(r, ctx));
}

function validateCondition(c: {field: string; op: string}, ctx: GridSpecContext) {
    check(isPlainObject(c), 'Malformed condition');
    checkField(c.field, ctx);
    check(CONDITION_OPERATORS.includes(c.op as any), `Unknown operator '${c.op}' - use one of ${CONDITION_OPERATORS.join(', ')}`);
}

function validateStyleRule(r: StyleRule, ctx: GridSpecContext) {
    check(isPlainObject(r), 'Malformed style rule');
    validateCondition(r.when, ctx);
    check(isPlainObject(r.style), 'A style rule needs a style');
    Object.entries(r.style).forEach(([part, value]) => {
        const values: readonly string[] = CELL_STYLE_VALUES[part];
        check(values, `Unknown style property '${part}' - use ${Object.keys(CELL_STYLE_VALUES).join(', ')}`);
        if (!isNil(value)) check(values.includes(value), `Unknown ${part} '${value}' - use one of ${values.join(', ')}`);
    });
}

function requireColumn(spec: GridSpec, field: string): GridSpecColumn {
    const col = findColumn(spec, field);
    check(col, `No column '${field}' in the grid`);
    return col;
}

function requireGroup(spec: GridSpec, groupId: string): GridSpecColumnGroup {
    const group = columnGroups(spec).find(g => g.groupId === groupId);
    check(group, `No column group '${groupId}'`);
    return group;
}

//------------------------
// Tree manipulation
//------------------------
function containerOf(spec: GridSpec, field: string): GridSpecColumnOrGroup[] {
    return findParentGroup(spec, field)?.children ?? spec.columns;
}

function removeFromParent(spec: GridSpec, field: string) {
    const container = containerOf(spec, field),
        col = container.find(c => !isColumnGroup(c) && c.field === field);
    pull(container, col);
}

function insertBefore(spec: GridSpec, field: string, col: GridSpecColumn) {
    const container = containerOf(spec, field);
    container.splice(
        container.findIndex(c => !isColumnGroup(c) && c.field === field),
        0,
        col
    );
}

function insertAfter(spec: GridSpec, field: string, col: GridSpecColumn) {
    const container = containerOf(spec, field);
    container.splice(container.findIndex(c => !isColumnGroup(c) && c.field === field) + 1, 0, col);
}

function insertAt<T>(container: T[], item: T, index: number) {
    if (isNumber(index) && index >= 0 && index < container.length) container.splice(index, 0, item);
    else container.push(item);
}

function leafIndex(spec: GridSpec, field: string): number {
    return leafColumns(spec).findIndex(c => c.field === field);
}
